import { useEffect, useRef, useState, type RefObject } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';

/**
 * Podświetlenie elementu kroku na żywo (przewodnik po zmianach, 21.10.2026):
 * obramowanie z pulsowaniem wokół elementu, przewinięcie do niego i linia ze
 * strzałką od panelu. Nic tu nie łapie kliknięć (`pointer-events: none`) —
 * operator pracuje dalej, podświetlenie tylko pokazuje miejsce.
 *
 * Element szukamy po selektorze co klatkę: ekran react-admina dociąga dane
 * i sekcje po chwili, a po przewinięciu albo przesunięciu panelu obramowanie
 * i linia idą za elementem. Po `SEARCH_MS` bez elementu — „nie ma go na
 * ekranie” (szukamy dalej: operator może właśnie otworzyć okno).
 */

/** Kolor podświetlenia — inny niż ostrzeżenia (pomarańcz) i błędy (czerwień). */
export const HIGHLIGHT_COLOR = '#7b1fa2';
export const SEARCH_MS = 4000;

export type HighlightStatus = 'searching' | 'found' | 'missing';

type Box4 = { left: number; top: number; width: number; height: number };

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(123, 31, 162, 0.45); }
  70% { box-shadow: 0 0 0 12px rgba(123, 31, 162, 0); }
  100% { box-shadow: 0 0 0 0 rgba(123, 31, 162, 0); }
`;

const raf = (cb: () => void): number =>
  typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(cb) : window.setTimeout(cb, 50);
const cancelRaf = (id: number) =>
  typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame(id) : window.clearTimeout(id);

/** Widoczny element pasujący do selektora (pierwszy z niezerowym wymiarem). */
export function findVisible(selector: string): HTMLElement | null {
  let nodes: NodeListOf<HTMLElement>;
  try {
    nodes = document.querySelectorAll<HTMLElement>(selector);
  } catch {
    return null;
  }
  for (let i = 0; i < nodes.length; i += 1) {
    const r = nodes[i].getBoundingClientRect();
    if (r.width > 0 || r.height > 0) return nodes[i];
  }
  return null;
}

const toBox = (r: DOMRect | Box4): Box4 => ({
  left: Math.round(r.left),
  top: Math.round(r.top),
  width: Math.round(r.width),
  height: Math.round(r.height),
});
const sameBox = (a: Box4 | null, b: Box4 | null) =>
  a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height);

/** Punkt na brzegu prostokąta najbliższy podanemu punktowi. */
export function edgePoint(box: Box4, toward: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.min(Math.max(toward.x, box.left), box.left + box.width),
    y: Math.min(Math.max(toward.y, box.top), box.top + box.height),
  };
}

const center = (b: Box4) => ({ x: b.left + b.width / 2, y: b.top + b.height / 2 });

export const GuideHighlight = ({
  selector,
  token,
  panelRef,
  onStatus,
}: {
  /** Selektor elementu; `null` = bez podświetlenia. */
  selector: string | null;
  /** Nowa wartość = nowa prośba (znów przewiń do elementu). */
  token: number;
  /** Panel przewodnika — początek linii; bez panelu (zwinięty) — samo obramowanie. */
  panelRef?: RefObject<HTMLElement | null>;
  onStatus?: (status: HighlightStatus) => void;
}) => {
  const [target, setTarget] = useState<Box4 | null>(null);
  const [panel, setPanel] = useState<Box4 | null>(null);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  useEffect(() => {
    if (!selector) {
      setTarget(null);
      return undefined;
    }
    let frame = 0;
    let scrolled = false;
    let reported: HighlightStatus | null = null;
    const started = Date.now();
    const report = (s: HighlightStatus) => {
      if (reported === s) return;
      reported = s;
      statusRef.current?.(s);
    };
    report('searching');
    const tick = () => {
      const el = findVisible(selector);
      if (el) {
        if (!scrolled) {
          scrolled = true;
          el.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
        const next = toBox(el.getBoundingClientRect());
        setTarget((prev) => (sameBox(prev, next) ? prev : next));
        report('found');
      } else {
        setTarget(null);
        if (Date.now() - started > SEARCH_MS) report('missing');
      }
      const p = panelRef?.current ? toBox(panelRef.current.getBoundingClientRect()) : null;
      setPanel((prev) => (sameBox(prev, p) ? prev : p));
      frame = raf(tick);
    };
    tick();
    return () => cancelRaf(frame);
  }, [selector, token, panelRef]);

  if (!selector || !target) return null;
  const pad = 4;
  const box: Box4 = { left: target.left - pad, top: target.top - pad, width: target.width + 2 * pad, height: target.height + 2 * pad };
  const line =
    panel && panel.width > 0
      ? { from: edgePoint(panel, center(box)), to: edgePoint(box, center(panel)) }
      : null;
  const lineLength = line ? Math.hypot(line.to.x - line.from.x, line.to.y - line.from.y) : 0;

  return (
    <>
      <Box
        data-testid="guide-highlight"
        aria-hidden
        sx={{
          position: 'fixed',
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          border: `3px solid ${HIGHLIGHT_COLOR}`,
          borderRadius: '6px',
          pointerEvents: 'none',
          zIndex: 1450,
          animation: `${pulse} 1.6s ease-out infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
      {line && lineLength > 24 ? (
        <svg
          data-testid="guide-highlight-line"
          aria-hidden
          style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 1449 }}
        >
          <defs>
            <marker id="guide-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={HIGHLIGHT_COLOR} />
            </marker>
          </defs>
          <line
            x1={line.from.x}
            y1={line.from.y}
            x2={line.to.x}
            y2={line.to.y}
            stroke={HIGHLIGHT_COLOR}
            strokeWidth={2}
            strokeDasharray="6 4"
            markerEnd="url(#guide-arrow)"
          />
        </svg>
      ) : null}
    </>
  );
};
